/** Something on a day grid, in minutes from the start of the day. `end` is exclusive. */
export type Span = { start: number; end: number };

/** A span with its column among the ones it overlaps: `col` of `cols`, counting from 0. */
export type Placed<T> = T & { col: number; cols: number };

/** Short events still get this much height, so they stay readable. */
export const MIN_MINUTES = 15;

/** Minutes from `from` to `iso`, clamped to the range, so events running in or on stop at its edges. */
export function minutesInto(range: { from: string; to: string }, iso: string): number {
  const start = Date.parse(range.from);
  const length = (Date.parse(range.to) - start) / 60_000;
  return Math.min(length, Math.max(0, (Date.parse(iso) - start) / 60_000));
}

/**
 * Puts overlapping spans side by side. Spans that touch a chain of overlaps share its column count,
 * and each takes the first column free when it starts.
 */
export function placeInColumns<T extends Span>(spans: T[]): Placed<T>[] {
  const sorted = [...spans].sort((a, b) => a.start - b.start || b.end - a.end);
  const placed: Placed<T>[] = [];
  let group: Placed<T>[] = [];
  let columnEnds: number[] = [];
  let groupEnd = -1;

  const flush = () => {
    for (const span of group) span.cols = columnEnds.length;
    group = [];
    columnEnds = [];
  };

  for (const span of sorted) {
    const end = Math.max(span.end, span.start + MIN_MINUTES);
    if (group.length > 0 && span.start >= groupEnd) flush();
    let col = columnEnds.findIndex((columnEnd) => columnEnd <= span.start);
    if (col < 0) col = columnEnds.push(0) - 1;
    columnEnds[col] = end;
    groupEnd = group.length === 0 ? end : Math.max(groupEnd, end);
    const item = { ...span, col, cols: 1 };
    group.push(item);
    placed.push(item);
  }
  flush();
  return placed;
}

/** The hours to draw: 07:00 to 21:00, widened to whole hours around anything earlier or later. */
export function hourWindow(spans: Span[], first = 7, last = 21): { first: number; last: number } {
  for (const { start, end } of spans) {
    first = Math.min(first, Math.floor(start / 60));
    last = Math.max(last, Math.ceil(end / 60));
  }
  return { first, last: Math.min(last, 24) };
}
